import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, Pencil } from "lucide-react";
import { NotaStatus, Lampiran } from "@/components/nota/NotaShared";
import { toast } from "sonner";

const fmtTime = (s) => (s ? new Date(s).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function NotaDetail({ id, onClose, onChanged, onEdit, onPreview }) {
  const { user } = useAuth();
  const [n, setN] = useState(null);
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/nota-pengajuan/${id}`).then((r) => setN(r.data)).catch((e) => { toast.error(apiErr(e)); onClose(); }); }, [id, onClose]);
  const sa = user?.role === "super_admin";
  const decide = async () => {
    if (mode === "tolak" && !text.trim()) return toast.error("Alasan penolakan wajib diisi");
    setBusy(true);
    try {
      const r = await api.post(`/nota-pengajuan/${id}/${mode}`, { catatan: text });
      setN(r.data); setMode(null); setText("");
      toast.success(mode === "setujui" ? "Nota disetujui" : "Nota ditolak");
      onChanged();
      window.dispatchEvent(new Event("nota-changed"));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="nota-detail">
        {!n ? <><DialogHeader><DialogTitle>Memuat nota…</DialogTitle></DialogHeader><div className="p-8 text-center text-slate-400">Memuat…</div></> : <>
          <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2"><span data-testid="nota-detail-nomor">{n.nomor}</span> <NotaStatus s={n.status} testid="nota-detail-status" /></DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info l="Tanggal Belanja" v={formatDate(n.tanggal)} /><Info l="Pengaju" v={n.pengaju_nama} />
            <Info l="Proyek" v={n.project_nama || "-"} /><Info l="Mandor" v={n.mandor_nama || "-"} />
            <Info l="Kategori" v={n.kategori || "-"} /><Info l="Diajukan" v={fmtTime(n.diajukan_at)} />
            <div className="col-span-2"><Info l="Keterangan" v={n.keterangan || "-"} /></div>
            {n.legacy && <div className="col-span-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600" data-testid="nota-detail-legacy">Nota lama (arsip) — dipindahkan dari nota belanja sebelumnya</div>}
          </div>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm" data-testid="nota-detail-items">
              <thead><tr className="bg-blue-900 text-white text-xs uppercase"><th className="px-3 py-2 text-left">Barang</th><th className="px-3 py-2 text-right">Jml</th><th className="px-3 py-2 text-right">Harga</th><th className="px-3 py-2 text-right">Subtotal</th></tr></thead>
              <tbody>{n.items.map((i, k) => <tr key={k} className="border-t"><td className="px-3 py-1.5">{i.nama}{i.keterangan && <span className="text-slate-400"> · {i.keterangan}</span>}</td><td className="px-3 py-1.5 text-right font-mono">{i.qty}</td><td className="px-3 py-1.5 text-right font-mono">{rupiah(i.harga)}</td><td className="px-3 py-1.5 text-right font-mono">{rupiah(i.subtotal)}</td></tr>)}</tbody>
              <tfoot><tr className="bg-orange-50 font-bold"><td colSpan={3} className="px-3 py-2">TOTAL BELANJA</td><td className="px-3 py-2 text-right font-mono" data-testid="nota-detail-total">{rupiah(n.total)}</td></tr></tfoot>
            </table>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-2">Bukti Nota (klik untuk memperbesar)</div>
            <div className="flex flex-wrap gap-3">{n.lampiran.length ? n.lampiran.map((l, k) => <Lampiran key={l.path} f={l} onOpen={onPreview} testid={`nota-detail-lampiran-${k}`} />) : <span className="text-sm text-slate-400">Belum ada lampiran</span>}</div>
          </div>
          {(n.status === "Disetujui" || n.status === "Ditolak") && (
            <div className={`rounded-lg p-3 text-sm ${n.status === "Disetujui" ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`} data-testid="nota-detail-review">
              <b>{n.status}</b> oleh {n.reviewed_by} · {fmtTime(n.reviewed_at)}
              {n.alasan_tolak && <div>Alasan: {n.alasan_tolak}</div>}{n.catatan_review && <div>Catatan: {n.catatan_review}</div>}
              {n.status === "Disetujui" && <div className="mt-1 text-xs opacity-80">Persetujuan berarti nota disetujui, bukan bukti pembayaran sudah dilakukan.</div>}
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Riwayat</div>
            <ul className="space-y-1 text-xs text-slate-600" data-testid="nota-detail-history">{n.riwayat.map((h, k) => <li key={k}><b>{h.aksi}</b> — {h.oleh} · {fmtTime(h.waktu)}{h.catatan ? ` · "${h.catatan}"` : ""}</li>)}</ul>
          </div>
          {mode && <div className="space-y-2" data-testid="nota-decide-box">
            <Textarea rows={2} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} data-testid="nota-decide-text"
              placeholder={mode === "tolak" ? "Alasan penolakan (wajib)" : "Catatan pemeriksaan (opsional)"} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" disabled={busy} onClick={() => { setMode(null); setText(""); }}>Batal</Button>
              <Button disabled={busy} className={mode === "setujui" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} onClick={decide} data-testid="nota-decide-confirm">
                {busy ? "Memproses…" : mode === "setujui" ? "Konfirmasi Setujui" : "Konfirmasi Tolak"}</Button>
            </div>
          </div>}
          <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
            {n.pengaju_id === user?.id && (n.status === "Draf" || n.status === "Ditolak") &&
              <Button variant="outline" onClick={() => onEdit(n)} data-testid="nota-detail-edit"><Pencil className="h-4 w-4 mr-1" /> {n.status === "Ditolak" ? "Perbaiki & Ajukan Ulang" : "Ubah Draf"}</Button>}
            {sa && n.status === "Menunggu Persetujuan" && !mode && <>
              <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => setMode("tolak")} data-testid="nota-reject-btn"><X className="h-4 w-4 mr-1" /> Tolak</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setMode("setujui")} data-testid="nota-approve-btn"><Check className="h-4 w-4 mr-1" /> Setujui</Button>
            </>}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ l, v }) => <div><div className="text-xs text-slate-500">{l}</div><div className="font-semibold text-slate-800 break-words">{v}</div></div>;
